/**
 * AdminVoiceIntakeModal — admin records 60-120s describing a new client.
 * AI fills Client core + Strengths/Needs + Risk + Client Info.
 *
 * Flow:
 *  1. Admin clicks "Voice intake" from AdminClients.
 *  2. Records monologue.
 *  3. Reviews extracted Client core; can edit critical fields inline; toggles
 *     which assessment sections to include.
 *  4. Clicks "Create client" → POST /clients then PUT 3 singletons in parallel.
 *  5. Parent (AdminClients) reloads + opens the new client modal.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2, Sparkles, X, Check, AlertTriangle, User, Heart, ShieldAlert, Info } from "lucide-react";
import useVoiceRecorder from "@/lib/useVoiceRecorder";

const SECTION_META = {
  client: { l: "Client profile", icon: User, color: "#0F172A" },
  strengths_needs: { l: "Strengths & Needs", icon: Heart, color: "#16A34A" },
  risk: { l: "Risk assessment", icon: ShieldAlert, color: "#EF4444" },
  info: { l: "Client information", icon: Info, color: "#0EA5E9" },
};

function ConfBadge({ value }) {
  if (value == null) return null;
  const v = Number(value) || 0;
  const tone = v >= 0.8 ? "#16A34A" : v >= 0.5 ? "#F59E0B" : "#EF4444";
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: tone + "1a", color: tone }}>
      {Math.round(v * 100)}%
    </span>
  );
}

function Row({ label, value }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2 py-0.5 text-sm">
      <span className="text-clinical-subtext text-xs uppercase tracking-wide">{label}</span>
      <span className="text-clinical-text">
        {Array.isArray(value) ? value.map((v, i) => (
          typeof v === "object" ? JSON.stringify(v) : <span key={i} className="mr-2">{v}</span>
        )) : typeof value === "boolean" ? (value ? "Yes" : "No") : String(value)}
      </span>
    </div>
  );
}

function ClientPreview({ data }) {
  if (!data) return null;
  return (
    <div>
      <Row label="Name" value={data.name} />
      <Row label="DOB / Age" value={data.dob || (data.age_years && `${data.age_years} yrs`)} />
      <Row label="Gender" value={data.gender} />
      <Row label="Address" value={data.address} />
      <Row label="Diagnosis" value={data.primary_diagnosis} />
      <Row label="Allergies" value={(data.allergies || []).map((a) =>
        `${a.substance}${a.severity ? ` (${a.severity})` : ""}`)} />
      <Row label="Diet" value={data.diet} />
      <Row label="Mobility" value={data.mobility} />
      <Row label="Cognition" value={data.cognition} />
      <Row label="Fall risk" value={data.fall_risk} />
      <Row label="Code status" value={data.code_status} />
      <Row label="Adv directive" value={data.advance_directive_on_file} />
      <Row label="PCP" value={data.doctor_name} />
      <Row label="Family" value={data.family_contact_name} />
      <Row label="Schedule" value={data.scheduled_days} />
      <Row label="Languages" value={data.required_languages} />
    </div>
  );
}

function StrengthsPreview({ data }) {
  if (!data) return null;
  return (
    <div>
      <Row label="Strengths" value={data.strengths} />
      <Row label="Interests" value={data.interests} />
      <Row label="Support" value={data.support_network} />
      <Row label="Physical" value={data.physical_needs} />
      <Row label="Cognitive" value={data.cognitive_needs} />
      <Row label="Medical" value={data.medical_needs} />
      <Row label="Safety" value={data.safety_needs} />
      <Row label="Goals" value={data.long_term_goals} />
    </div>
  );
}

function RiskPreview({ data }) {
  if (!data) return null;
  const domains = [
    ["overall_risk", "Overall"],
    ["fall_risk", "Fall"], ["elopement_risk", "Elopement"],
    ["choking_aspiration_risk", "Choking"], ["aggression_risk", "Aggression"],
    ["medication_self_admin_risk", "Med self-admin"],
    ["skin_breakdown_risk", "Skin"], ["infection_risk", "Infection"],
  ];
  return (
    <div>
      <Row label="Summary" value={data.summary} />
      <div className="grid grid-cols-2 gap-1 mt-2">
        {domains.map(([k, l]) => data[k] && (
          <Row key={k} label={l} value={data[k]} />
        ))}
      </div>
    </div>
  );
}

function InfoPreview({ data }) {
  if (!data) return null;
  return (
    <div>
      <Row label="PCP" value={data.pcp_name + (data.pcp_phone ? ` · ${data.pcp_phone}` : "")} />
      <Row label="Pharmacy" value={data.pharmacy_name + (data.pharmacy_phone ? ` · ${data.pharmacy_phone}` : "")} />
      <Row label="Hospice" value={data.hospice_provider} />
      <Row label="Pref hospital" value={data.preferred_hospital} />
      <Row label="Living" value={data.living_arrangement} />
      <Row label="POA" value={data.poa_name + (data.poa_type ? ` (${data.poa_type})` : "")} />
      <Row label="Guardian" value={data.legal_guardian_name} />
      <Row label="Language" value={data.primary_language} />
      <Row label="Marital" value={data.marital_status} />
      <Row label="Religion" value={data.religion} />
    </div>
  );
}

const PREVIEW = {
  client: ClientPreview, strengths_needs: StrengthsPreview,
  risk: RiskPreview, info: InfoPreview,
};

export default function AdminVoiceIntakeModal({ open, onClose, onCreated }) {
  const [result, setResult] = useState(null);
  const [editName, setEditName] = useState("");
  const [selected, setSelected] = useState({});
  const [creating, setCreating] = useState(false);

  const rec = useVoiceRecorder({
    endpoint: "/ai/voice-fill/client-intake",
    filenamePrefix: "intake",
    limitMs: 8 * 60 * 1000,  // intake monologues run longer
    timeoutMs: 120000,
    onResult: (data) => {
      setResult(data);
      setEditName(data.client?.name || "");
      setSelected({
        client: !!(data.client?.name || data.client?.primary_diagnosis),
        strengths_needs: !!(data.strengths_needs?.strengths?.length || data.strengths_needs?.medical_needs),
        risk: !!(data.risk?.overall_risk || data.risk?.fall_risk),
        info: !!(data.info?.pcp_name || data.info?.poa_name),
      });
    },
  });

  const close = () => {
    rec.reset(); setResult(null); setSelected({}); setEditName(""); onClose?.();
  };

  const create = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    setCreating(true);
    try {
      // 1. Build client payload from the extracted client object
      const c = result.client || {};
      const clientPayload = {
        name: editName.trim(),
        address: c.address || "",
        dob: c.dob || "",
        gender: c.gender || "",
        pronouns: c.pronouns || "",
        primary_diagnosis: c.primary_diagnosis || "",
        allergies: (c.allergies || []).filter((a) => a?.substance),
        diet: c.diet || "",
        diet_notes: c.diet_notes || "",
        mobility: c.mobility || "",
        cognition: c.cognition || "",
        fall_risk: c.fall_risk || "",
        code_status: c.code_status || "",
        advance_directive_on_file: !!c.advance_directive_on_file,
        doctor_name: c.doctor_name || "",
        doctor_phone: c.doctor_phone || "",
        family_contact_name: c.family_contact_name || "",
        family_contact_phone: c.family_contact_phone || "",
        emergency_note: c.emergency_note || "",
        scheduled_days: c.scheduled_days || [],
        required_languages: c.required_languages || [],
        notes: c.notes || "",
        assigned_caregiver_ids: [],
      };
      const created = await api.post("/clients", clientPayload);
      const cid = created.data.id;

      // 2. PUT the singleton assessments in parallel — best effort
      const tasks = [];
      if (selected.strengths_needs && result.strengths_needs) {
        const sn = Object.fromEntries(
          Object.entries(result.strengths_needs).filter(([, v]) => v != null && v !== "")
        );
        if (Object.keys(sn).length) {
          tasks.push(api.put(`/clients/${cid}/strengths-needs`, sn).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("intake: strengths-needs PUT failed", e?.response?.data);
          }));
        }
      }
      if (selected.risk && result.risk) {
        const risk = Object.fromEntries(
          Object.entries(result.risk).filter(([, v]) => v != null && v !== "")
        );
        if (Object.keys(risk).length) {
          tasks.push(api.put(`/clients/${cid}/risk-assessment`, risk).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("intake: risk-assessment PUT failed", e?.response?.data);
          }));
        }
      }
      if (selected.info && result.info) {
        const info = Object.fromEntries(
          Object.entries(result.info).filter(([, v]) => v != null && v !== "")
        );
        if (Object.keys(info).length) {
          tasks.push(api.put(`/clients/${cid}/client-info`, info).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("intake: client-info PUT failed", e?.response?.data);
          }));
        }
      }
      await Promise.all(tasks);
      toast.success(`${editName.trim()} created via voice intake`);
      onCreated?.(cid);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create client");
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="voice-intake-modal">
      <div className="card-surface w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-clinical-border px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "#7C3AED1a", color: "#7C3AED" }}>
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="font-semibold text-clinical-text">Voice intake — new client</h2>
              <p className="text-xs text-clinical-subtext">
                {rec.isIdle && !result && "Describe the new client — 60-120 seconds covers everything."}
                {rec.isRecording && `Recording · ${rec.mmss}`}
                {rec.isProcessing && "Transcribing + extracting 4 forms…"}
                {result && "Review what AI extracted, then create."}
              </p>
            </div>
          </div>
          <button type="button" onClick={close}
            className="rounded-lg p-2 text-clinical-subtext hover:bg-clinical-bg"
            aria-label="Close" data-testid="voice-intake-close">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-3">
          {!result && (
            <div className="flex flex-col items-center gap-4 py-8">
              {rec.isIdle && (
                <p className="text-sm text-center text-clinical-subtext max-w-xl">
                  Example: <i>"Eleanor Brooks, 82, Alzheimer's stage 2. Allergic to penicillin, severe. DNR. Uses a walker. Diabetic diet. High fall risk. PCP is Dr. Stevens at 555-1234. CVS pharmacy 555-9876. Daughter Lisa is medical POA at 555-7890. Lives alone at 142 Oak Street. Loves crosswords. Scheduled Mon Wed Fri mornings."</i>
                </p>
              )}
              {rec.isRecording && (
                <div className="flex flex-col items-center gap-2">
                  <div className="relative h-24 w-24">
                    <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />
                    <div className="relative h-24 w-24 rounded-full bg-red-500 flex items-center justify-center">
                      <Mic className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="text-2xl font-mono tabular-nums text-clinical-text" data-testid="voice-intake-timer">
                    {rec.mmss}
                  </div>
                </div>
              )}
              {rec.isProcessing && (
                <>
                  <Loader2 className="h-10 w-10 animate-spin text-clinical-accent" />
                  <p className="text-sm text-clinical-subtext">
                    Whisper → Claude Sonnet — 15-30 seconds…
                  </p>
                </>
              )}
              <button
                type="button"
                onClick={rec.toggle}
                disabled={rec.isProcessing}
                className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition ${
                  rec.isRecording ? "bg-red-600 text-white hover:bg-red-700"
                                  : "bg-clinical-accent text-white hover:opacity-90"
                } disabled:opacity-50`}
                data-testid={rec.isRecording ? "voice-intake-stop" : "voice-intake-start"}
              >
                {rec.isRecording ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-4 w-4" />}
                {rec.isRecording ? "Stop & process" : rec.isProcessing ? "Processing…" : "Start recording"}
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="voice-intake-results">
              {result.transcript && (
                <details className="card-surface p-3 bg-clinical-bg/40">
                  <summary className="cursor-pointer text-xs font-semibold uppercase text-clinical-subtext">
                    Transcript
                  </summary>
                  <p className="mt-2 text-sm text-clinical-text whitespace-pre-line">{result.transcript}</p>
                </details>
              )}
              {(result.warnings || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <AlertTriangle size={12} /> Warnings
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              {/* Editable client name (required) */}
              <div className="card-surface p-3">
                <label htmlFor="intake-name" className="label-eyebrow">Client name (required)</label>
                <input
                  id="intake-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input-clinical mt-1"
                  data-testid="voice-intake-name"
                />
              </div>

              {/* Per-section toggles + preview */}
              {Object.entries(SECTION_META).map(([k, m]) => {
                const PreviewC = PREVIEW[k];
                const sectionData = result[k];
                const isEmpty = !sectionData || Object.values(sectionData).every((v) =>
                  v == null || v === "" || (Array.isArray(v) && v.length === 0)
                );
                return (
                  <div key={k} className={`card-surface p-3 ${isEmpty ? "opacity-50" : ""}`}
                    data-testid={`voice-intake-section-${k}`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selected[k] && !isEmpty}
                        disabled={isEmpty || k === "client"}  /* client always required */
                        onChange={(e) => setSelected({ ...selected, [k]: e.target.checked })}
                        className="h-4 w-4 rounded text-clinical-accent"
                        data-testid={`voice-intake-toggle-${k}`}
                      />
                      <m.icon size={14} style={{ color: m.color }} />
                      <span className="font-medium text-sm text-clinical-text">{m.l}</span>
                      <ConfBadge value={result.confidence?.[k]} />
                    </label>
                    <div className="mt-2 pl-6">
                      <PreviewC data={sectionData} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {result && (
          <div className="border-t border-clinical-border px-5 py-3 flex items-center justify-between">
            <button type="button" onClick={() => { setResult(null); rec.reset(); }}
              className="text-sm text-clinical-subtext hover:text-clinical-text"
              data-testid="voice-intake-redo">
              Record again
            </button>
            <button type="button" onClick={create} disabled={creating || !editName.trim()}
              className="btn-primary" data-testid="voice-intake-create">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check size={14} />}
              Create client
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
